import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Select, Input, message, Modal, Table } from 'antd';
import { FaPlus, FaMinus, FaSave, FaEdit, FaChevronUp, FaChevronDown, FaSearch, FaArrowUp, FaArrowDown, FaGripVertical } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import { api, API_BASE_URL } from '../../../config/api';
import { toast } from "react-toastify";
import CountrySelectInput from "../../shared/countrySelectInput";
import { useMyContext } from "../../../context/MyContext";

const { Option } = Select;
const NOM_HS_CODES_LIST = "NOM HS Codes List";
const HS_CODE_STARTS_WITH_KEYS = ["HSCode Starts with", "Shipment HS Code Starts With"];

const isNomService = (description = "") => description.trim().toLowerCase() === "nom";

const normalizeNomServiceValues = (service) => {
  if (!isNomService(service.description) || !Array.isArray(service.values)) {
    return service;
  }

  const hsCodeIndex = service.values.findIndex((value) => HS_CODE_STARTS_WITH_KEYS.includes(value));
  if (hsCodeIndex === -1 || service.values.includes(NOM_HS_CODES_LIST)) {
    return service;
  }

  const thenIndex = service.values.indexOf("then", hsCodeIndex);
  if (thenIndex === -1 || thenIndex <= hsCodeIndex + 1) {
    return service;
  }

  return {
    ...service,
    values: [
      ...service.values.slice(0, hsCodeIndex + 1),
      NOM_HS_CODES_LIST,
      ...service.values.slice(thenIndex),
    ],
  };
};

const normalizeNomServices = (services = []) => services.map(normalizeNomServiceValues);

const tempData = [
  {
    label: "IOR",
    items: [
      "IOR",
      "Export documentation",
      "Customs clearance",
      "Handling",
      "Collection",
      "Airfreight",
      "Administration",
      "Duties and taxes",
      "Compliance",
      "VAT",
      "Final delivery",
      "Destination charges",
      "Leiability coverage",
      "Approvals",
      "Import permit",
      "Bank fees",
      "Validation",
      "THC",
      "Insurance"
    ],
  },
  {
    label: "EOR",
    items: [
      "EOR",
      "Export clearance",
      "Export documentation",
      "Collection",
      "Airfreight",
      "Handling",
      "Administration",
      "Bank fees",
      "Export compliance",
      "Insurance"
    ],
  },
  {
    label: "EOR & IOR",
    items: [
      "EOR",
      "Export documentation",
      "IOR",
      "Customs clearance",
      "Handling",
      "Collection",
      "Airfreight",
      "Administration",
      "Duties and taxes",
      "Compliance",
      "VAT",
      "Final delivery",
      "Destination charges",
      "Leiability coverage",
      "Approvals",
      "Import permit",
      "Bank fees",
      "Validation",
      "THC",
      "Insurance"
    ],
  },
];

const valueOptions = [
  { value: "+", label: "+" },
  { value: "-", label: "-" },
  { value: "*", label: "*" },
  { value: "/", label: "/" },
  { value: "=", label: "=" },
  { value: "%", label: "%" },
  { value: ">", label: ">" },
  { value: "<", label: "<" },
  { value: ">=", label: ">=" },
  { value: "<=", label: "<=" },
  { value: "(", label: "(" },
  { value: ")", label: ")" },
  { value: "Pending", label: "?" },
  { value: ":", label: ":" },
  { value: "if", label: "if" },
  { value: "else if", label: "else if" },
  { value: "else", label: "else" },
  { value: "then", label: "then" },
  { value: "min", label: "min" },
  { value: "max", label: "max" },
  { value: ",", label: "," },
];

const ServiceTempIOR = ({
  formData,
  setFormData,
  setTemplateSelected,
  validationErrors,
  setValidationErrors,
  savedServices,
  setSavedServices,
  calculatedValues = null
}) => {
  const { user } = useMyContext();
  const [services, setServices] = useState([]);
  const [disabledSaveButtons, setDisabledSaveButtons] = useState({});
  const [highlightedFields, setHighlightedFields] = useState({});
  const [descriptionErrors, setDescriptionErrors] = useState({});
  const [savedServiceIndices, setSavedServiceIndices] = useState([]);
  const [emptyFields, setEmptyFields] = useState({});
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [editMode, setEditMode] = useState({});
  const [isExistingServiceModalVisible, setIsExistingServiceModalVisible] = useState(false);
  const [existingEstimations, setExistingEstimations] = useState([]);
  const [selectedEstimation, setSelectedEstimation] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isTableExpanded, setIsTableExpanded] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [templateHistories, setTemplateHistories] = useState({});

  // New states for pagination and loading
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100']
  });
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // New state for detailed estimate data
  const [estimateDetails, setEstimateDetails] = useState(null);
  const [loadingEstimateDetails, setLoadingEstimateDetails] = useState(false);

  // Use useRef for debounced search to fix the cancel issue
  const debouncedSearchRef = useRef(null);

  // Create debounced search function with proper cancel method
  const createDebouncedSearch = useCallback(() => {
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }

    return {
      execute: (value) => {
        if (debouncedSearchRef.current) {
          clearTimeout(debouncedSearchRef.current);
        }
        debouncedSearchRef.current = setTimeout(() => {
          fetchExistingEstimationsOptimized(1, pagination.pageSize, value);
          setSearchLoading(false);
        }, 800);
      },
      cancel: () => {
        if (debouncedSearchRef.current) {
          clearTimeout(debouncedSearchRef.current);
          debouncedSearchRef.current = null;
        }
      }
    };
  }, [pagination.pageSize]);

  const debouncedSearch = createDebouncedSearch();

  useEffect(() => {
    if (calculatedValues && formData?.selectedTemplate?.services) {
      const newValidationErrors = { ...validationErrors };

      formData.selectedTemplate.services.forEach((service, index) => {
        const calculatedValue = calculatedValues[service.description];
        const hasOnlyQuestionMark = service.values.length === 1 && service.values[0] === 'Pending';

        if (isNaN(calculatedValue) || hasOnlyQuestionMark) {
          newValidationErrors[`service_${index}`] = 'Invalid service calculation';
        } else {
          delete newValidationErrors[`service_${index}`];
        }
      });

      setValidationErrors(newValidationErrors);
    }
  }, [formData.selectedTemplate.services, calculatedValues, setValidationErrors, validationErrors]);

  useEffect(() => {
    if (!formData.selectedTemplate.name) {
      setTemplateSelected(false);
    } else {
      setTemplateSelected(true);
      const normalizedServices = normalizeNomServices(formData.selectedTemplate.services);
      setServices(normalizedServices);
      setSavedServices(normalizedServices);

      if (JSON.stringify(normalizedServices) !== JSON.stringify(formData.selectedTemplate.services)) {
        setFormData((prevData) => ({
          ...prevData,
          selectedTemplate: {
            ...prevData.selectedTemplate,
            services: normalizedServices,
          },
        }));
      }
    }
  }, [formData.selectedTemplate, setFormData, setTemplateSelected, setSavedServices]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current);
      }
    };
  }, []);

  const handleTemplateChange = (templateLabel) => {
    const selectedTemplateData = tempData.find(
      (template) => template.label === templateLabel
    );

    if (selectedTemplateData) {
      let newServices;

      if (user.role !== 'user' && templateHistories[templateLabel]) {
        newServices = templateHistories[templateLabel];
      } else {
        newServices = selectedTemplateData.items.map((description) => ({
          description,
          values: [],
        }));
      }

      if (user.role !== 'user' && formData.selectedTemplate.name && services.length > 0) {
        setTemplateHistories(prev => ({
          ...prev,
          [formData.selectedTemplate.name]: [...services]
        }));
      }

      setServices(newServices);
      setFormData((prevData) => ({
        ...prevData,
        selectedTemplate: {
          name: templateLabel,
          services: newServices,
        },
      }));
    }
  };

  const handleAddValue = (e, serviceIndex) => {
    e.preventDefault();
    const currentService = services[serviceIndex];

    const emptyValueIndices = currentService.values.reduce((acc, value, index) => {
      if (value === "") {
        acc.push(index);
      }
      return acc;
    }, []);

    if (emptyValueIndices.length > 0) {
      toast.error("Please fill in all existing values before adding a new one.");

      setHighlightedFields(prev => ({
        ...prev,
        [serviceIndex]: emptyValueIndices
      }));

      setTimeout(() => {
        setHighlightedFields(prev => {
          const newState = { ...prev };
          delete newState[serviceIndex];
          return newState;
        });
      }, 3000);

      return;
    }

    const updatedServices = services.map((service, index) => {
      if (index === serviceIndex) {
        return { ...service, values: [...service.values, ""] };
      }
      return service;
    });
    updateServices(updatedServices);
  };

  const handleRemoveValue = (serviceIndex, valueIndex) => {
    const updatedServices = services.map((service, index) => {
      if (index === serviceIndex) {
        return {
          ...service,
          values: service.values.filter((_, i) => i !== valueIndex),
        };
      }
      return service;
    });
    updateServices(updatedServices);
  };

  const handleValueChange = (serviceIndex, valueIndex, newValue) => {
    const updatedServices = services.map((service, index) => {
      if (index === serviceIndex) {
        const newValues = [...service.values];
        newValues[valueIndex] = newValue;
        return { ...service, values: newValues };
      }
      return service;
    });
    updateServices(updatedServices);
  };

  const handleRemoveService = (e, serviceIndex) => {
    e.preventDefault();
    const updatedServices = services.filter(
      (_, index) => index !== serviceIndex
    );
    updateServices(updatedServices);
  };

  const handleDescriptionChange = (serviceIndex, newDescription) => {
    const updatedServices = services.map((service, index) => {
      if (index === serviceIndex) {
        return { ...service, description: newDescription };
      }
      return service;
    });
    updateServices(updatedServices);
  };

  const handleAddService = (e) => {
    e.preventDefault();
    const newService = {
      description: "",
      values: [],
    };
    updateServices([...services, newService]);
  };

  const updateServices = (updatedServices) => {
    setServices(updatedServices);
    setSavedServices(updatedServices);
    setFormData((prevData) => ({
      ...prevData,
      selectedTemplate: {
        ...prevData.selectedTemplate,
        services: updatedServices,
      },
    }));

    if (user.role !== 'user' && formData.selectedTemplate.name) {
      setTemplateHistories(prev => ({
        ...prev,
        [formData.selectedTemplate.name]: [...updatedServices]
      }));
    }
  };

  const validateService = (service) => {
    if (!service.description || service.description.trim() === '') {
      return 'Service description is required';
    }
    if (service.values.length === 0) {
      return 'At least one value is required';
    }
    for (let i = 0; i < service.values.length; i++) {
      if (service.values[i] === '') {
        return `Value at position ${i + 1} cannot be empty`;
      }
    }
    return null;
  };

  const isOperator = (value) => {
    const operators = ['+', '-', '*', '/', '%', '(', ')'];
    return operators.includes(value);
  };

  const isValue = (value) => {
    return !isOperator(value) && value.trim() !== '';
  };

  const validateFormula = (values) => {
    if (values.some(value => ['if', 'min', 'max'].includes(value.trim()))) {
      return { isValid: true };
    }

    let openParentheses = 0;
    let expectValue = true;

    for (let i = 0; i < values.length; i++) {
      const currentValue = values[i].trim();

      if (currentValue === '(') {
        openParentheses++;
        expectValue = true;
      } else if (currentValue === ')') {
        if (openParentheses === 0 || expectValue) {
          return { isValid: false, errorIndex: i, message: `Misplaced closing parenthesis at position ${i + 1}` };
        }
        openParentheses--;
        expectValue = false;
      } else if (isOperator(currentValue)) {
        if (expectValue && currentValue !== '-') {
          return { isValid: false, errorIndex: i, message: `Unexpected operator at position ${i + 1}` };
        }
        expectValue = true;
      } else if (isValue(currentValue)) {
        if (!expectValue) {
          return { isValid: false, errorIndex: i, message: `Unexpected value at position ${i + 1}` };
        }
        expectValue = false;
      } else {
        return { isValid: false, errorIndex: i, message: `Invalid token at position ${i + 1}` };
      }
    }

    if (openParentheses !== 0) {
      return { isValid: false, errorIndex: values.length - 1, message: 'Unmatched parentheses in the formula' };
    }

    if (expectValue) {
      return { isValid: false, errorIndex: values.length - 1, message: 'Formula ends with an operator or opening parenthesis' };
    }

    return { isValid: true };
  };

  const handleSaveService = (e, serviceIndex) => {
    e.preventDefault();
    const service = services[serviceIndex];

    setAttemptedSave(true);

    const newEmptyFields = {};
    if (!service.description.trim()) {
      newEmptyFields.description = true;
    }

    const nonEmptyValues = service.values.filter(value => value.trim() !== '');
    if (nonEmptyValues.length === 0) {
      newEmptyFields.values = [0];
      toast.error("Please add at least one value to the service.");
      setEmptyFields(prev => ({
        ...prev,
        [serviceIndex]: newEmptyFields
      }));
      return;
    }

    const validationResult = validateFormula(service.values);
    if (!validationResult.isValid) {
      newEmptyFields.values = [validationResult.errorIndex];
      toast.error(validationResult.message);
      setEmptyFields(prev => ({
        ...prev,
        [serviceIndex]: newEmptyFields
      }));
      return;
    }

    service.values.forEach((value, index) => {
      if (!value.trim()) {
        if (!newEmptyFields.values) newEmptyFields.values = [];
        newEmptyFields.values.push(index);
      }
    });

    if (Object.keys(newEmptyFields).length > 0) {
      setEmptyFields(prev => ({
        ...prev,
        [serviceIndex]: newEmptyFields
      }));
      toast.error("Please fill in all required fields before saving.");
      return;
    }

    const updatedSavedServices = [...savedServices];
    updatedSavedServices[serviceIndex] = service;
    setSavedServices(updatedSavedServices);

    setDisabledSaveButtons(prev => ({ ...prev, [serviceIndex]: true }));
    setSavedServiceIndices(prev => [...prev, serviceIndex]);

    setEmptyFields(prev => {
      const newState = { ...prev };
      delete newState[serviceIndex];
      return newState;
    });
    setAttemptedSave(false);
    setEditMode(prev => ({ ...prev, [serviceIndex]: false }));

    toast.success('Service saved successfully!');
  };

  const handleEditService = (e, serviceIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setEditMode(prev => ({ ...prev, [serviceIndex]: true }));
    setDisabledSaveButtons(prev => ({ ...prev, [serviceIndex]: false }));
  };

  const calculateServiceValue = (values) => {
    try {
      const expression = values
        .map((v) => {
          const savedService = savedServices.find((s) => s.description === v);
          return savedService ? savedService.calculatedValue : v;
        })
        .join(" ");
      return eval(expression).toString();
    } catch (error) {
      console.error("Error calculating service value:", error);
      return "Error";
    }
  };

  const renderSelectOption = (serviceIndex, valueIndex) => {
    const service = services[serviceIndex];
    const value = services[serviceIndex].values[valueIndex];
    const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
    const isHighlighted = attemptedSave && emptyFields[serviceIndex]?.values?.includes(valueIndex);
    const isEditable = editMode[serviceIndex] || !disabledSaveButtons[serviceIndex];

    const selectStyle = {
      width: "100%",
      border: isHighlighted ? "1px solid red" : undefined,
      borderRadius: "8px",
      opacity: isEditable ? 1 : 0.7,
      pointerEvents: isEditable ? 'auto' : 'none',
    };

    if (services[serviceIndex].values[valueIndex - 1] === "COO") {
      return (
        <div className="w-full" style={{ minWidth: '200px' }}>
          <CountrySelectInput
            name={`country-${serviceIndex}-${valueIndex}`}
            id={`country-${serviceIndex}-${valueIndex}`}
            placeholder="Select Country"
            value={value}
            onChange={(selectedCountry) => {
              if (isEditable) {
                handleValueChange(serviceIndex, valueIndex, selectedCountry.target.value);
                clearEmptyFieldHighlight(serviceIndex, valueIndex);
              }
            }}
            className={`w-full ${isHighlighted ? 'border-red-500' : ''} ${!isEditable ? 'opacity-70 cursor-not-allowed' : ''}`}
            disabled={!isEditable}
          />
        </div>
      );
    }

    const hasMinOrMax = service.values.includes("min") || service.values.includes("max") || service.values.some((value) => HS_CODE_STARTS_WITH_KEYS.includes(value));
    const isNomHsCodeListSlot = isNomService(service.description) && HS_CODE_STARTS_WITH_KEYS.includes(service.values[valueIndex - 1]);

    const options = [
      {
        key: "arithmetic-label",
        label: "Arithmetic Operations",
        options: valueOptions.filter(option =>
          hasMinOrMax ? true : option.value !== ","
        )
      },
      {
        key: "saved-services-label",
        label: "Saved Services",
        options: services
          .filter((_, index) => index < serviceIndex && savedServiceIndices.includes(index))
          .map(service => ({
            value: service.description,
            label: service.description,
          })),
      },
      {
        key: "shipment-info-label",
        label: "Shipment Information",
        options: [
          { value: "Invoice Value", label: "Shipment Value" },
          { value: "Total Item Quantity", label: "Item Quantity" },
          { value: "Item Weight", label: "Shipment Weight" },
          { value: "Volume Weight", label: "Chargeable Weight" },
          { value: "Airfreight Volume", label: "Airfreight Volume" },
          { value: "Airfreight Charges", label: "Airfreight Charges" },
          { value: "Quantity", label: "Box Quantity(Pallet)" },
          { value: "HSCode Count", label: "Shipment HS Code Count" },
          { value: "HSCode Starts with", label: "Shipment HS Code Starts With" },
          ...(isNomHsCodeListSlot ? [{ value: NOM_HS_CODES_LIST, label: NOM_HS_CODES_LIST }] : []),
          { value: "COO", label: "Country of Origin" },
          { value: "Insurance Underwriter", label: "Insurance Underwriter" },
          { value: "CIF", label: "CIF" },
          { value: "CIF110", label: "CIF 1.10" },
          { value: "Insurance", label: "Insurance" },
          { value: "IOR", label: "IOR" },
          { value: "Handling Charges", label: "Handling Charges" },
          { value: "Administration", label: "Administration" },
          { value: "Customs Clearance A Class", label: "Customs Clearance A Class" },
          { value: "Handling A Class", label: "Handling A Class" },
          { value: "Administration A Class", label: "Administration A Class" },
          { value: "THC A Class", label: "THC A Class" },
          { value: "Customs Clearance B Class", label: "Customs Clearance B Class" },
          { value: "THC B Class", label: "THC B Class" },
          { value: "Handling B Class", label: "Handling B Class" },
          { value: "Administration B Class", label: "Administration B Class" },
          { value: "Customs Clearance C Class", label: "Customs Clearance C Class" },
          { value: "Handling C Class", label: "Handling C Class" },
          { value: "Administration C Class", label: "Administration C Class" },
          { value: "THC C Class", label: "THC C Class" },
          { value: "document", label: "Document Required" },
          { value: "handling", label: "Request OUS Handling" },
        ],
      },
    ];

    if (isNumeric) {
      options.push({
        key: "numeric-value",
        label: "Numeric Value",
        options: [{ value: value, label: value }],
      });
    }

    return (
      <Select
        style={selectStyle}
        value={value || undefined}
        onChange={(newValue) => {
          if (isEditable) {
            handleValueChange(serviceIndex, valueIndex, newValue);
            clearEmptyFieldHighlight(serviceIndex, valueIndex);
          }
        }}
        className={`flex-grow mr-2 ${isHighlighted ? 'border-red-500' : ''}`}
        showSearch
        optionFilterProp="label"
        filterOption={(input, option) => {
          return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }}
        onSearch={(input) => {
          if (isEditable && !isNaN(parseFloat(input)) && isFinite(input)) {
            handleValueChange(serviceIndex, valueIndex, input);
            clearEmptyFieldHighlight(serviceIndex, valueIndex);
          }
        }}
        disabled={!isEditable}
      >
        {options.map(group => (
          <Select.OptGroup key={group.key} label={group.label}>
            {group.options.map(option => (
              <Select.Option key={option.value} value={option.value} label={option.label}>
                {option.label}
              </Select.Option>
            ))}
          </Select.OptGroup>
        ))}
      </Select>
    );
  };

  const clearEmptyFieldHighlight = (serviceIndex, valueIndex) => {
    setEmptyFields(prev => {
      const newState = { ...prev };
      if (newState[serviceIndex]?.values) {
        newState[serviceIndex].values = newState[serviceIndex].values.filter(i => i !== valueIndex);
        if (newState[serviceIndex].values.length === 0) delete newState[serviceIndex].values;
        if (Object.keys(newState[serviceIndex]).length === 0) delete newState[serviceIndex];
      }
      return newState;
    });
  };

  const handleAddExistingService = () => {
    setIsExistingServiceModalVisible(true);
    setIsTableExpanded(true);
    setSelectedEstimation(null);
    setSelectedServices([]);
    setEstimateDetails(null);
    setPagination(prev => ({ ...prev, current: 1 }));
    setSearchTerm('');
    setSearchLoading(false);
    debouncedSearch.cancel();
    fetchExistingEstimationsOptimized(1, pagination.pageSize, '');
  };

  // OPTIMIZED FETCH FUNCTION
  const fetchExistingEstimationsOptimized = async (page = 1, limit = 10, search = '') => {
    setLoading(true);
    try {
      const response = await api.get(`${API_BASE_URL}/estimates/existing-services-optimized`, {
        params: {
          page,
          limit,
          search: search.trim(),
          searchField: 'unloadCountry'
        }
      });

      setExistingEstimations(response.data.data);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: limit,
        total: response.data.pagination.total,
      }));
    } catch (error) {
      console.error("Error fetching existing estimations (optimized):", error);
      toast.error("Failed to fetch existing estimations");
      setExistingEstimations([]);
    } finally {
      setLoading(false);
    }
  };

  // FUNCTION TO FETCH ESTIMATE DETAILS
  const fetchEstimateDetails = async (estimateId) => {
    setLoadingEstimateDetails(true);
    try {
      const response = await api.get(`${API_BASE_URL}/estimates/estimate-services/${estimateId}`);
      setEstimateDetails(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching estimate details:", error);
      toast.error("Failed to fetch estimate details");
      return null;
    } finally {
      setLoadingEstimateDetails(false);
    }
  };

  // ESTIMATION SELECT HANDLER
  const handleEstimationSelect = async (estimation) => {
    setSelectedEstimation(estimation);
    setIsTableExpanded(false);

    if (!estimation.selectedTemplate?.services || estimation.selectedTemplate.services.length === 0) {
      const details = await fetchEstimateDetails(estimation.estimateId);
      if (details) {
        setSelectedEstimation(prev => ({
          ...prev,
          selectedTemplate: details.selectedTemplate
        }));
      }
    }
  };

  const handleServiceSelect = (selectedServiceDescriptions) => {
    setSelectedServices(selectedServiceDescriptions);
  };

  const handleAddSelectedServices = () => {
    const estimationToUse = estimateDetails || selectedEstimation;

    if (estimationToUse && selectedServices.length > 0) {
      const newServices = selectedServices.map(serviceDescription => {
        const service = estimationToUse.selectedTemplate.services.find(s => s.description === serviceDescription);
        return {
          description: service.description,
          values: service.values,
        };
      });

      const updatedServices = [...services, ...newServices];
      updateServices(updatedServices);

      setIsExistingServiceModalVisible(false);
      setSelectedEstimation(null);
      setSelectedServices([]);
      setEstimateDetails(null);
      toast.success("Selected services added successfully");
    } else {
      toast.error("Please select an estimation and at least one service");
    }
  };

  const columns = [
    {
      title: 'Estimate ID',
      dataIndex: 'estimateId',
      key: 'estimateId',
      width: 100,
      sorter: true,
    },
    {
      title: 'Load Country',
      dataIndex: 'loadCountry',
      key: 'loadCountry',
      render: (loadCountry) => {
        const countries = Array.isArray(loadCountry) ? loadCountry : [loadCountry];
        return countries.length > 2
          ? `${countries.slice(0, 2).join(', ')} +${countries.length - 2} more`
          : countries.join(', ');
      },
      width: 200,
    },
    {
      title: 'Unload Country',
      dataIndex: 'unloadCountry',
      key: 'unloadCountry',
      width: 150,
    },
    {
      title: 'Currency',
      dataIndex: 'airfreightCurrency',
      key: 'airfreightCurrency',
      width: 100,
    },
    {
      title: 'Weight Unit',
      dataIndex: 'afcWeightUnit',
      key: 'afcWeightUnit',
      width: 100,
    },
    {
      title: 'Template',
      dataIndex: ['selectedTemplate', 'name'],
      key: 'selectedTemplate',
      width: 120,
    },
    {
      title: 'Services',
      key: 'servicesCount',
      render: (_, record) => {
        const servicesCount = record.servicesCount || (record.selectedTemplate?.services?.length) || 0;
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {servicesCount} services
          </span>
        );
      },
      width: 100,
    },
  ];

  // SEARCH HANDLER with proper debouncing
  const handleSearch = (value) => {
    setSearchTerm(value);

    // Cancel any pending searches
    debouncedSearch.cancel();

    if (value.trim() === '') {
      setSearchLoading(true);
      fetchExistingEstimationsOptimized(1, pagination.pageSize, '');
      setSearchLoading(false);
    } else if (value.trim().length >= 2) {
      setSearchLoading(true);
      debouncedSearch.execute(value);
    } else {
      setSearchLoading(false);
    }
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo, filters, sorter) => {
    const { current, pageSize } = paginationInfo;
    fetchExistingEstimationsOptimized(current, pageSize, searchTerm);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const updatedServices = Array.from(services);
    const [movedService] = updatedServices.splice(sourceIndex, 1);
    updatedServices.splice(destinationIndex, 0, movedService);

    updateServices(updatedServices);
  };

  const moveService = (index, direction) => {
    const updatedServices = Array.from(services);
    if (direction === 'up' && index > 0) {
      [updatedServices[index], updatedServices[index - 1]] = [updatedServices[index - 1], updatedServices[index]];
    } else if (direction === 'down' && index < updatedServices.length - 1) {
      [updatedServices[index], updatedServices[index + 1]] = [updatedServices[index + 1], updatedServices[index]];
    }
    updateServices(updatedServices);
  };

  const renderService = (service, serviceIndex) => (
    <Draggable draggableId={`service-${serviceIndex}`} index={serviceIndex} key={`service-${serviceIndex}`}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`mb-6 border rounded p-4 relative ${validationErrors[`service_${serviceIndex}`] ? 'border-red-500' : ''
            } ${snapshot.isDragging ? 'shadow-lg bg-white' : ''}`}
        >
          <div className="absolute top-2 right-2 flex items-center gap-2">
            <div
              {...provided.dragHandleProps}
              className="cursor-move p-2 hover:bg-gray-100 rounded transition-colors flex items-center"
            >
              <FaGripVertical className="text-gray-500" />
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => moveService(serviceIndex, 'up')}
                className={`text-gray-600 hover:text-gray-800 p-2 rounded transition-colors ${serviceIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
                  }`}
                disabled={serviceIndex === 0}
                type="button"
              >
                <FaArrowUp />
              </button>
              <button
                onClick={() => moveService(serviceIndex, 'down')}
                className={`text-gray-600 hover:text-gray-800 p-2 rounded transition-colors ${serviceIndex === services.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
                  }`}
                disabled={serviceIndex === services.length - 1}
                type="button"
              >
                <FaArrowDown />
              </button>
            </div>
            {disabledSaveButtons[serviceIndex] ? (
              <button
                onClick={(e) => handleEditService(e, serviceIndex)}
                className="flex items-center text-white rounded px-2 py-1 bg-[#03336E] hover:bg-[#022247] transition-colors"
                type="button"
              >
                <FaEdit className="mr-2" /> Edit
              </button>
            ) : (
              <button
                onClick={(e) => handleSaveService(e, serviceIndex)}
                className={`flex items-center text-white rounded px-2 py-1 transition-colors ${disabledSaveButtons[serviceIndex]
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#03336E] hover:bg-[#022247]'
                  }`}
                disabled={disabledSaveButtons[serviceIndex]}
                type="button"
              >
                <FaSave className="mr-2" /> Save
              </button>
            )}
          </div>

          <div className="flex items-center mb-4 mt-8">
            <Input
              type="text"
              value={service.description}
              onChange={(e) => {
                if (editMode[serviceIndex] || !disabledSaveButtons[serviceIndex]) {
                  handleDescriptionChange(serviceIndex, e.target.value);
                  if (emptyFields[serviceIndex]?.description) {
                    setEmptyFields(prev => {
                      const newState = { ...prev };
                      delete newState[serviceIndex].description;
                      if (Object.keys(newState[serviceIndex]).length === 0) delete newState[serviceIndex];
                      return newState;
                    });
                  }
                }
              }}
              className={`w-1/4 mr-2 ${(attemptedSave && emptyFields[serviceIndex]?.description) ? 'border-red-500' : ''}`}
              readOnly={!editMode[serviceIndex] && disabledSaveButtons[serviceIndex]}
            />
            {service.values.length === 0 && (
              <button
                type="button"
                onClick={(e) => handleAddValue(e, serviceIndex)}
                className="flex items-center text-[#03336E]"
                disabled={!editMode[serviceIndex] && disabledSaveButtons[serviceIndex]}
              >
                <FaPlus className="mr-2" /> Add Formula
              </button>
            )}
          </div>
          <div className="grid grid-cols-4 gap-4 mb-4">
            {service.values.map((_, valueIndex) => (
              <div key={valueIndex} className="flex items-center">
                {renderSelectOption(serviceIndex, valueIndex)}
                {(editMode[serviceIndex] || !disabledSaveButtons[serviceIndex]) && (
                  <div
                    className="mx-[5px] cursor-pointer"
                    onClick={() => handleRemoveValue(serviceIndex, valueIndex)}
                  >
                    <FaMinus className="text-[16px] text-[#03336E]" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 flex justify-between">
            {service.values.length > 0 && (editMode[serviceIndex] || !disabledSaveButtons[serviceIndex]) && (
              <button
                type="button"
                onClick={(e) => handleAddValue(e, serviceIndex)}
                className="flex items-center text-[#03336E]"
              >
                <FaPlus className="mr-2" /> Add Value
              </button>
            )}
            {(editMode[serviceIndex] || !disabledSaveButtons[serviceIndex]) && (
              <button
                onClick={(e) => handleRemoveService(e, serviceIndex)}
                className="flex items-center text-[#03336E]"
              >
                <FaMinus className="mr-2" /> Remove Service
              </button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );

  return (
    <div className="p-4 ml-4">
      <div className="mb-4">
        <label className="block mb-2">Select Template <span className="text-red-500">*</span></label>
        <Select
          style={{ width: 200 }}
          onChange={handleTemplateChange}
          value={formData.selectedTemplate.name}
          options={tempData.map((temp) => ({
            label: temp.label,
            value: temp.label,
          }))}
        />
        {validationErrors.selectedTemplate && (
          <p className="text-red-500 text-sm mt-1">{validationErrors.selectedTemplate}</p>
        )}
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="services">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-4"
            >
              {services.map((service, serviceIndex) => renderService(service, serviceIndex))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="flex items-center mt-4">
        <button
          onClick={(e) => handleAddService(e)}
          className="flex items-center text-[#03336E] mr-4"
          type="button"
        >
          <FaPlus className="mr-2" /> Add New Service
        </button>
        <button
          onClick={handleAddExistingService}
          className="flex items-center text-[#03336E]"
          type="button"
        >
          <FaPlus className="mr-2" /> Add Existing Service
        </button>
      </div>

      <Modal
        title={<h3 className="text-xl font-semibold">Add Existing Service</h3>}
        visible={isExistingServiceModalVisible}
        onCancel={() => {
          setIsExistingServiceModalVisible(false);
          setIsTableExpanded(true);
          setSelectedEstimation(null);
          setSelectedServices([]);
          setEstimateDetails(null);
          setSearchTerm('');
          debouncedSearch.cancel();
        }}
        footer={null}
        width={1000}
        className="rounded-lg shadow-lg"
      >
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-lg font-semibold">Existing Estimations</h4>
            <div className="flex items-center">
              <div className="relative mr-2">
                <Input
                  placeholder="Search by unload country..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onPressEnter={(e) => {
                    if (e.target.value.trim()) {
                      setSearchLoading(true);
                      debouncedSearch.cancel();
                      fetchExistingEstimationsOptimized(1, pagination.pageSize, e.target.value);
                    }
                  }}
                  className="pl-10 pr-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300 w-64"
                  suffix={searchLoading && <span className="animate-spin text-blue-500">⏳</span>}
                  allowClear
                  onClear={() => {
                    setSearchTerm('');
                    setSearchLoading(false);
                    debouncedSearch.cancel();
                    fetchExistingEstimationsOptimized(1, pagination.pageSize, '');
                  }}
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
              <button
                onClick={() => setIsTableExpanded(!isTableExpanded)}
                className="text-blue-500 hover:text-blue-700 transition-colors p-2"
                title={isTableExpanded ? "Collapse table" : "Expand table"}
              >
                {isTableExpanded ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>
          </div>

          {isTableExpanded && (
            <div className="border rounded-lg overflow-hidden">
              <Table
                dataSource={existingEstimations}
                columns={columns}
                rowKey="estimateId"
                loading={loading}
                onRow={(record) => ({
                  onClick: () => handleEstimationSelect(record),
                  className: `${record.estimateId === selectedEstimation?.estimateId
                    ? 'bg-blue-100 border-2 border-blue-300'
                    : 'hover:bg-gray-50'} cursor-pointer transition-colors`
                })}
                pagination={{
                  ...pagination,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
                  onShowSizeChange: (current, size) => handleTableChange({ current: 1, pageSize: size }),
                  showQuickJumper: true,
                  size: 'small'
                }}
                scroll={{ y: 400, x: 800 }}
                locale={{
                  emptyText: loading
                    ? 'Loading estimations...'
                    : (searchTerm && searchTerm.trim().length >= 2)
                      ? `No results found for "${searchTerm}"`
                      : 'No estimations found'
                }}
                size="small"
              />
            </div>
          )}
        </div>

        {selectedEstimation && (
          <div className="mt-6 border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-lg font-semibold">
                Selected: #{selectedEstimation.estimateId} - {selectedEstimation.unloadCountry}
              </h4>
              {loadingEstimateDetails && (
                <span className="text-sm text-gray-500 flex items-center">
                  <span className="animate-spin mr-2">⏳</span>
                  Loading services...
                </span>
              )}
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><strong>Load Countries:</strong> {Array.isArray(selectedEstimation.loadCountry) ? selectedEstimation.loadCountry.join(', ') : selectedEstimation.loadCountry}</div>
                <div><strong>Template:</strong> {selectedEstimation.selectedTemplate?.name}</div>
                <div><strong>Currency/Unit:</strong> {selectedEstimation.airfreightCurrency} / {selectedEstimation.afcWeightUnit}</div>
              </div>
            </div>

            {!loadingEstimateDetails && (estimateDetails || selectedEstimation.selectedTemplate?.services) && (
              <>
                <h5 className="text-md font-medium mb-2">Available Services:</h5>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Select services to add to your current template"
                  onChange={handleServiceSelect}
                  value={selectedServices}
                  optionFilterProp="label" // Use label for filtering instead of children
                  filterOption={(input, option) => {
                    // Safely handle the filtering with proper type checking
                    const label = option.label || option.children;
                    if (typeof label === 'string') {
                      return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }
                    // If label is not a string, convert option value to string for comparison
                    const value = option.value || '';
                    return value.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  }}
                  className="w-full"
                  maxTagCount="responsive"
                >
                  {((estimateDetails || selectedEstimation).selectedTemplate?.services || []).map((service, index) => (
                    <Select.Option
                      key={`${service.description}-${index}`}
                      value={service.description}
                      label={`${service.description} (${service.values?.length || 0} values)`} // Add explicit label prop
                    >
                      {service.description} ({service.values?.length || 0} values)
                    </Select.Option>
                  ))}
                </Select>

                {selectedServices.length > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-md border-l-4 border-blue-400">
                    <p className="text-sm font-medium text-blue-700 mb-2">
                      Selected {selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''}:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedServices.map((serviceName, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-200 text-blue-800">
                          {serviceName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end border-t pt-4 gap-3">
          <button
            onClick={() => {
              setIsExistingServiceModalVisible(false);
              setIsTableExpanded(true);
              setSelectedEstimation(null);
              setSelectedServices([]);
              setEstimateDetails(null);
              setSearchTerm('');
              debouncedSearch.cancel();
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAddSelectedServices}
            className={`px-4 py-2 rounded-md transition-colors ${!selectedEstimation || selectedServices.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-[#03336E] text-white hover:bg-[#022247]'
              }`}
            disabled={!selectedEstimation || selectedServices.length === 0}
          >
            Add Selected Services ({selectedServices.length})
          </button>
        </div>
      </Modal>
    </div>
  );
};



export default ServiceTempIOR;
